import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";

import style from "./style";

/**
 * Checkbox component that allows users to toggle a checkbox.
 *
 * @component
 * @param {boolean} props.defaultValue - The initial defaultValue of the checkbox.
 * @param {function} props.onChange - The callback function called when the checkbox defaultValue changes.
 * @returns {JSX.Element} The rendered Checkbox component.
 */
const Checkbox = ({ defaultValue, onChange, onClick, error, ariaLabel, id, inverted }) => {
  const [checked, setChecked] = useState(defaultValue || false);
  const [hasError, setHasError] = useState(error || false);

  // Handles the checkbox change event
  const handleCheckboxChange = (event) => {
    !checked && setHasError(false);
    setChecked(!checked);
    onChange(event.target.checked);
  };

  useEffect(() => {
    if (defaultValue !== checked) setChecked(!checked);
  }, [defaultValue]);

  useEffect(() => {
    error && setHasError(error);
  }, [error]);

  if (!onChange || typeof onChange !== "function") {
    return null;
  }

  return (
    <div className="checkbox" onClick={onClick}>
      <label className="checkbox--content" htmlFor={id}>
        <input
          className="checkbox--content--input"
          id={id}
          type="checkbox"
          checked={checked}
          onChange={handleCheckboxChange}
          aria-label={ariaLabel}
        />
        <span
          className={
            inverted
              ? "checkbox--content--checkmark--inverted"
              : !hasError
              ? "checkbox--content--checkmark"
              : "checkbox--content--checkmark--error"
          }
        />
      </label>
      <style jsx>{style}</style>
    </div>
  );
};

Checkbox.propTypes = {
  defaultValue: PropTypes.bool, // Initial defaultValue of the checkbox
  onChange: PropTypes.func.isRequired, // Callback function for checkbox change event
  onClick: PropTypes.func,
};

export default Checkbox;
